import { useCallback, useMemo } from 'react';

import { TFunction } from '@lyearn/i18n';
import useDebounceState from '@/hooks/useDebounceState';
import useTag from '@/modules/corporate/modules/tags/views/TagsEditor/hooks/useTag';
import useTags from '@/modules/corporate/modules/tags/views/TagsManager/hooks/useTags';
import { DateTagType, TagTypeEnum } from '@/types/schema';

export type AutoCompleteDateTag = {
  id: string;
  name: string;
};

export const useDateTagAutoComplete = (t: TFunction, selectedTagId?: string) => {
  const [searchText, setSearchText, debounceSearchStr] = useDebounceState('');
  const onInputChange = useCallback(
    (val: string) => {
      setSearchText(val || '');
    },
    [setSearchText],
  );

  const { tags, fetchMore, isLoading } = useTags({
    filters: {
      name: debounceSearchStr,
      type: [TagTypeEnum.Date],
    },
  });

  const { tag: selectedTag, fetching: fetchingTag } = useTag({
    tagId: selectedTagId,
    pause: !selectedTagId,
  });

  const selectedTagValue: AutoCompleteDateTag | undefined = useMemo(
    () =>
      selectedTag
        ? {
            id: selectedTag.id,
            name: selectedTag.name,
          }
        : undefined,
    [selectedTag],
  );

  const tagsWithDateAndTime: AutoCompleteDateTag[] = useMemo(
    () =>
      (tags as DateTagType[])
        .filter((tag) => !tag.includeEndDate)
        .map((tag) => ({
          id: tag.id,
          name: tag.name,
        })),
    [tags],
  );

  return {
    tags: tagsWithDateAndTime,
    searchText,
    onInputChange,
    isLoading,
    fetchMore,
    selectedTag: selectedTagValue,
    fetchingSelected: fetchingTag,
  };
};
